// AMFooterTableByDataGrid.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Paper, Button, Card, CardHeader, Box } from "@mui/material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

import {
  AddBoxOutlined as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import { getCaption } from "src/utils/dictionary";

import GridFooterWithInlineForm from "./GridFooterWithInlineForm"; // adjust path if needed
import { getNextSerial, saveSubFormRow, deleteSubFormRow } from "src/utils/amUtils";

function AMFooterTableByDataGrid({
  title,
  columns,
  tableData,
  setTableData,
  subFormState,
  setSubFormState,
  subFormErrors,
  validateSubFormData,
  editForm,
}) {
  const [openInlineForm, setOpenInlineForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
const [confirmOpen, setConfirmOpen] = useState(false);
const [pendingDeleteRow, setPendingDeleteRow] = useState(null);
  const footerRef = useRef(null);

  const isEditMode = !!subFormState?.id && subFormState.id !== "add";

  const askDeleteRow = (row) => {
  setPendingDeleteRow(row);
  setConfirmOpen(true);
};

const closeConfirm = () => {
  setConfirmOpen(false);
  setPendingDeleteRow(null);
};

const confirmDelete = () => {
  try {
    // if delete requested from footer form, delete subFormState
    // if delete requested from row action, delete that row
    const rowToDelete = pendingDeleteRow || subFormState;
    setTableData(deleteSubFormRow(tableData || [], rowToDelete));
    closeConfirm();

    // if user deleted from footer form, close the form
    if (!pendingDeleteRow) {
      closeForm();
    }
  } catch (e) {
    toast.error(getCaption("system_error_try_later"));
    closeConfirm();
  }
};

  const scrollToFooter = () => {
    setTimeout(() => {
      footerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 50);
  };

  // Prepare add state after any table change (next serial)
  useEffect(() => {
    setSubFormState((prev) => ({
      ...(prev || {}),
      id: "add",
      serial: getNextSerial(tableData || []),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData]);

  const openAdd = () => {
    setSubFormState({
      id: "add",
      serial: getNextSerial(tableData || []),
    });
    setOpenInlineForm(true);
    // scrollToFooter();
  };

  const openEdit = (row) => {
    setSubFormState((prev) => ({
      ...(prev || {}),
      ...row,
      id: row.id,
    }));
    setOpenInlineForm(true);
    // Don't scroll when editing - modal will appear at bottom
  };

  const closeForm = () => {
    setOpenInlineForm(false);
    setSubFormState({
      id: "add",
      serial: getNextSerial(tableData || []),
    });
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (subFormErrors && Object.keys(subFormErrors).length > 0) return;

    setIsSubmitting(true);
    const ok = await validateSubFormData();

    if (ok) {
      try {
        setTableData(saveSubFormRow(tableData || [], subFormState));
        closeForm();
      } catch (e) {
        toast.error(getCaption("system_error_try_later"));
      }
    }

    setIsSubmitting(false);
  };

  const handleSubmitAndNew = async () => {
    if (isSubmitting) return;
    if (subFormErrors && Object.keys(subFormErrors).length > 0) return;

    setIsSubmitting(true);
    const ok = await validateSubFormData();

    if (ok) {
      try {
        const newTable = saveSubFormRow(tableData || [], subFormState);
        setTableData(newTable);

        setSubFormState({
          id: "add",
          serial: getNextSerial(newTable),
        });
        setOpenInlineForm(true);
        scrollToFooter();
      } catch (e) {
        toast.error(getCaption("system_error_try_later"));
      }
    }

    setIsSubmitting(false);
  };

  const handleDeleteCurrent = () => {
    try {
      setTableData(deleteSubFormRow(tableData || [], subFormState));
      closeForm();
    } catch (e) {
      toast.error(getCaption("system_error_try_later"));
    }
  };

  const deleteRow = useCallback(
    (rowData) => {
      try {
        setTableData(deleteSubFormRow(tableData || [], rowData));
      } catch (e) {
        toast.error(getCaption("system_error_try_later"));
      }
    },
    [setTableData, tableData]
  );

  const cols = useMemo(() => {
    const actionsColumn = {
      field: "actions",
      headerName: (
        <Button
          // variant="outlined"
          startIcon={<AddIcon />}
          color="primary"
          onClick={openAdd}
          size="large"
        >
          {getCaption("add_row")}
        </Button>
      ),
      type: "actions",
      width: 60,
      getActions: (params) => [
        <GridActionsCellItem
          key="edit"
          icon={<EditIcon />}
          label={getCaption("edit")}
         color="primary"
          onClick={() => openEdit(params.row)}
        />,
        <GridActionsCellItem
          key="delete"
          icon={<DeleteIcon />}
          label={getCaption("delete")}
          color="error"
         onClick={() => askDeleteRow(params.row)}
        />,
      ],
    };

    return [actionsColumn,...(columns?.length ? columns : [])];
  }, [columns, deleteRow]);

  return (
    <Card mb={6}>
      {/* <CardHeader
        title={title || "Details"}
      /> */}

      {/* <Paper> */}
        {/* <Box
          sx={{
            "& .MuiDataGrid-columnHeadersInner": {
              backgroundColor: "secondary.dark",
              color: "#FFF",
            },
            height: openInlineForm ? 520 : (tableData?.length || 0) > 1 ? 450 : 280,
            width: "100%",
          }}
        > */}
          <DataGrid
            rows={tableData || []}
            columns={cols}
            // pageSize={100}
            editMode="cell"
            autoHeight={true}
            density="compact"
            rowsPerPageOptions={[10, 50, 100]}
            disableSelectionOnClick

            // Lock grid "tools" while editing (optional but useful)
            disableColumnMenu={openInlineForm}
            disableColumnFilter={openInlineForm}
            disableColumnSelector={openInlineForm}
            disableDensitySelector={openInlineForm}
            // For MUI X DataGrid v5:
            components={{
              Footer: GridFooterWithInlineForm,
            }}
            componentsProps={{
              footer: {
                footerRef,
                openInlineForm,
                closeForm,
                isEditMode,
                editForm,
                subFormErrors,
                isSubmitting,
                handleSubmit,
                handleSubmitAndNew,
                handleDeleteCurrent: () => askDeleteRow(null),
              },
            }}
//to wrap header text
             sx={{
    '& .MuiDataGrid-columnHeaderTitle': {
      whiteSpace: 'normal',
      lineHeight: '1.2',
      textAlign: 'center', // optional
      height: 'auto',
    },
    '& .MuiDataGrid-columnHeader': {
      height: 'auto',
    },
    '& .MuiDataGrid-columnHeaders': {
      height: 'auto',
    },
  }}
          />
          <Dialog open={confirmOpen} onClose={closeConfirm}>
  <DialogTitle>{getCaption("confirm_delete")}</DialogTitle>
  <DialogContent>
    <DialogContentText>
      {getCaption("delete_record_cannot_undo")}
    </DialogContentText>
  </DialogContent>
  <DialogActions>
    <Button onClick={closeConfirm} variant="outlined">
      {getCaption("cancel")}
    </Button>
    <Button onClick={confirmDelete} color="error" variant="contained">
      {getCaption("delete")}
    </Button>
  </DialogActions>
</Dialog>

        {/* </Box> */}
      {/* </Paper> */}
    </Card>
  );
}

export default AMFooterTableByDataGrid;
